import React, { useState, useEffect } from 'react';
import { ENV_CONFIG } from '../../config/environment';
import { ServiceControlPanel } from './ServiceControlPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, Plus, Settings, ExternalLink, TestTube } from 'lucide-react';

interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'error';
  port: number;
  url: string;
  lastCheck: string;
  response?: any;
}

interface ServicesManagementProps {
  className?: string;
}

export const ServicesManagement: React.FC<ServicesManagementProps> = ({ className }) => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, string>>({});

  // Real service endpoints
  const serviceEndpoints = [
    { name: 'Smart-Ops Backend', port: 8000, url: ENV_CONFIG.SMART_OPS_BACKEND_URL || ENV_CONFIG.SMART_OPS_BACKEND_URL },
    { name: 'Smart-Ops Frontend', port: parseInt(String(ENV_CONFIG.SMART_OPS_FRONTEND_PORT || '9101')), url: ENV_CONFIG.FRONTEND_URL || ENV_CONFIG.FRONTEND_URL },
    { name: 'AI Service', port: 8001, url: ENV_CONFIG.AI_SERVICE_URL || ENV_CONFIG.AI_SERVICE_URL },
    { name: 'RAG Service', port: 8002, url: ENV_CONFIG.RAG_SERVICE_URL || ENV_CONFIG.RAG_SERVICE_URL },
  ];

  // Check service health
  const checkServiceHealth = async (service: typeof serviceEndpoints[0]): Promise<ServiceStatus> => {
    const endpoints = ['/health', '/', '/api/v1/health', '/status'];
    
    for (const endpoint of endpoints) {
      try {
        const response = await fetch(`${service.url}${endpoint}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(3000)
        });
        
        if (response.ok) {
          let responseData;
          try {
            responseData = await response.json();
          } catch {
            responseData = { message: 'Service responding' };
          }
          
          return {
            name: service.name,
            status: 'online',
            port: service.port,
            url: service.url,
            lastCheck: new Date().toISOString(),
            response: responseData
          };
        }
      } catch (error) {
        continue;
      }
    }
    
    return {
      name: service.name,
      status: 'offline',
      port: service.port,
      url: service.url,
      lastCheck: new Date().toISOString()
    };
  };

  // Test service connection
  const testServiceConnection = async (service: ServiceStatus) => {
    setTestResults(prev => ({ ...prev, [service.name]: 'Testing...' }));
    
    try {
      const result = await checkServiceHealth(serviceEndpoints.find(s => s.name === service.name)!);
      const status = result.status === 'online' ? 'Connection successful' : 'Connection failed';
      setTestResults(prev => ({ ...prev, [service.name]: status }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, [service.name]: 'Connection failed' }));
    }
  };

  // Load real data
  const loadRealData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const serviceChecks = await Promise.allSettled(
        serviceEndpoints.map(checkServiceHealth)
      );
      
      const serviceResults = serviceChecks.map(result => 
        result.status === 'fulfilled' ? result.value : {
          name: 'Unknown Service',
          status: 'offline' as const,
          port: 0,
          url: '',
          lastCheck: new Date().toISOString()
        }
      );
      
      setServices(serviceResults);
      
    } catch (err) {
      setError('Failed to load service data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRealData();
    const interval = setInterval(loadRealData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'offline': return 'bg-red-500';
      case 'error': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className={`services-management-content ${className}`}>
        <div className="loading-state">
          <div className="flex items-center justify-center h-32" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 loading-spinner" aria-label="Loading services"></div>
            <p className="ml-3">Checking service status...</p>
            <span className="sr-only">Loading services...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`services-management-content ${className}`}>
        <div className="error-state">
          <div className="text-red-500 text-center" role="alert" aria-live="assertive">{error}</div>
          <Button onClick={loadRealData} className="mt-2">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`services-management-content ${className}`}>
      {/* Header with actions */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Service Management</h2>
          <p className="text-gray-600">Monitor and manage your infrastructure services</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadRealData}
            className="flex items-center gap-2"
            aria-label="Refresh all services"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Refresh All
          </Button>
          <Button
            className="flex items-center gap-2"
            aria-label="Add new service"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add Service
          </Button>
        </div>
      </div>

      {/* Service Control Panel - Reuse existing component */}
      <ServiceControlPanel className="mb-6" />

      {/* Real-time Service Status Grid - Match E2E test expectations */}
      <div className="service-management-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" role="list" aria-label="Service status list">
          {services.map((service, index) => (
            <div 
              key={index} 
              className={`service-card border rounded-lg p-4 transition-colors ${
                selectedService === service.name ? 'ring-2 ring-blue-500 bg-blue-50' : ''
              }`}
              role="listitem"
              tabIndex={0}
              onClick={() => setSelectedService(service.name)}
              aria-label={`Service: ${service.name}, Status: ${getStatusText(service.status)}, Port: ${service.port}`}
            >
              <div className="service-card-header flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{service.name}</h3>
                  <p className="text-sm text-gray-600">Port {service.port}</p>
                </div>
                <div className="service-status-badge" role="status" aria-live="polite">
                  <Badge className={`${getStatusColor(service.status)} text-white`}>
                    {getStatusText(service.status)}
                  </Badge>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="detail-item flex justify-between">
                  <span className="text-gray-600">Port:</span>
                  <span className="font-mono">{service.port}</span>
                </div>
                <div className="detail-item flex justify-between">
                  <span className="text-gray-600">URL:</span>
                  <span className="font-mono text-xs truncate max-w-[150px]" title={service.url}>
                    {service.url}
                  </span>
                </div>
                <div className="detail-item flex justify-between">
                  <span className="text-gray-600">Last Check:</span>
                  <span>{new Date(service.lastCheck).toLocaleTimeString()}</span>
                </div>
              </div>
              
              {/* Test result display */}
              {testResults[service.name] && (
                <div className="response-info mt-2 p-2 bg-gray-50 rounded text-xs">
                  <span className="font-medium">Status: </span>
                  <span className={testResults[service.name].includes('successful') ? 'text-green-600' : 'text-red-600'}>
                    {testResults[service.name]}
                  </span>
                </div>
              )}
              
              <div className="service-actions flex gap-2 mt-3" role="group" aria-label={`Actions for ${service.name}`}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(service.url, '_blank');
                  }}
                  disabled={service.status === 'offline'}
                  className="btn-sm flex-1"
                  aria-label={`Open ${service.name} in new tab`}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Open Service
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    testServiceConnection(service);
                  }}
                  className="btn-sm flex-1"
                  aria-label={`Test connection to ${service.name}`}
                >
                  <TestTube className="h-3 w-3 mr-1" />
                  Test Connection
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Service Summary */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Service Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{services.length}</div>
              <div className="text-sm text-gray-600">Total Services</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {services.filter(s => s.status === 'online').length}
              </div>
              <div className="text-sm text-gray-600">Running</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {services.filter(s => s.status === 'offline').length}
              </div>
              <div className="text-sm text-gray-600">Stopped</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {services.filter(s => s.status === 'error').length}
              </div>
              <div className="text-sm text-gray-600">Errors</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 